import React, { useState, createRef } from 'react';
import { toast } from 'bulma-toast';
import PropTypes from 'prop-types';

const DuplicateAppealForm = function DuplicateAppealForm(props) {
    const {
        appealkey, newtitle, legacyurl, baseurl, error,
    } = props;
    const {
        host, protocol,
    } = new URL(baseurl);
    const parentUrl = document.referrer;

    const [newTitle, setNewTitle] = useState(newtitle);
    const inputRef = createRef();

    const inputHandler = (event) => {
        setNewTitle(event.target.value);
    };

    const boxStyle = {
        position: 'relative',
        margin: 'auto',
        marginBottom: 20,
        marginTop: 20,
        maxWidth: 700,
    };

    const toastConfig = {
        position: 'bottom-center',
        duration: 5000,
        closeOnClick: false,
        pauseOnHover: true,
        type: 'is-info is-light',
    };

    const successToast = () => toast({
        ...toastConfig,
        message: 'You are now using the most up to date template. You will be redirected in 5 seconds.',
    });

    const processingToast = () => toast({
        ...toastConfig,
        message: 'Processing - Please wait',
    });

    const redirectToLogin = () => {
        const returnUrl = `?ReturnUrl=${encodeURIComponent(parentUrl)}${encodeURIComponent('/Account/Appeal')}`;
        const url = new URL(`${protocol}//${host}/Auth/Account/Login${returnUrl}`);
        setTimeout(() => {
            window.parent.location.href = url;
        }, 5000);
    };

    const handleMessage = (message, responseStatus) => {
        // review error message - CLOUD-927
        if (responseStatus === 409) {
            toast({
                ...toastConfig,
                message: `${message}`,
            });
        } else if (responseStatus === 401) {
            toast({
                ...toastConfig,
                message: `${message}`,
            });
            redirectToLogin();
        } else {
            toast({
                ...toastConfig,
                message: `Please contact support@giveeasy.org quoting the error so we can assist you. \n\n${message}`,
            });
        }
    };

    const duplicateButtonHandler = () => {
        // show user feedback so they don't start the process again
        processingToast();

        const request = {
            method: 'POST',
            body: new URLSearchParams({ appealKey: appealkey, newTitle }),
            headers: new Headers({ 'content-type': 'application/x-www-form-urlencoded;charset=UTF-8' }),
        };

        let responseStatus = 0;
        return fetch(`${protocol}//${host}/Portal/Appeal/Duplicate`, request)
            .then((response) => {
                responseStatus = response.status;

                if (response.ok) {
                    // redirect the user to appropriate page.
                    return response.json().then((data) => {
                        const legacyUrl = new URL(legacyurl);

                        // show toast and redirect after 5 seconds
                        successToast();

                        setTimeout(() => {
                            window.parent.location.href = `${legacyUrl.protocol}//${legacyUrl.host}/Account/Appeal/${data.path}?uuid=${data.pageUUID}`;
                        }, 5000);
                    });
                }

                // we got something else that wasn't the expected response.
                return response.text().then((data) => handleMessage(data, responseStatus));
            })
            .catch((message) => {
                handleMessage(message, responseStatus);
            });
    };

    const upgradeButtonHandler = () => {
        // show user feedback so they don't start the process again
        processingToast();

        const request = {
            method: 'POST',
            body: new URLSearchParams({ appealKey: appealkey }),
            headers: new Headers({ 'content-type': 'application/x-www-form-urlencoded;charset=UTF-8' }),
        };

        let responseStatus = 0;
        return fetch(`${protocol}//${host}/Portal/Appeal/Upgrade`, request)
            .then((response) => {
                responseStatus = response.status;
                if (response.ok) {
                    // redirect the user to appropriate page.
                    return response.json().then(() => {
                        const legacyUrl = new URL(legacyurl);

                        // show toast and redirect after 5 seconds
                        successToast();

                        setTimeout(() => {
                            // Sorry! little hacky cause sick of cross domain
                            // window.location.reload() doesn't work due to cors
                            // and setting location to itself doesn't reload the page
                            const path = window.location.href.includes('index') ? '' : 'index';

                            window.parent.location.href = `${legacyUrl.protocol}//${legacyUrl.host}/Account/Appeal/${path}`;
                        }, 5000);
                    });
                }

                // we got something else that wasn't the expected response.
                return response.text().then((data) => handleMessage(data, responseStatus));
            })
            .catch((message) => {
                handleMessage(message, responseStatus);
            });
    };

    const upgradeForm = (
        <div className="box" style={boxStyle}>
            <div className="columns">
                <div className="column" style={{ flexGrow: 6, flexShrink: 7 }}>
                    <p style={{ marginBottom: 20 }}>
                        <span className="is-size-5">
                            Upgrade existing page
                        </span>
                        <br />
                        <span className="is-size-7">
                            <i>
                                (Keep current appeal title and URL while ensuring you are using
                                <br />
                                the most up-to-date template)
                            </i>
                        </span>
                    </p>
                </div>
                <div className="column">
                    <button style={{ marginTop: 40 }} type="button" className="button is-info is-large" onClick={upgradeButtonHandler}>Upgrade Page</button>
                </div>
            </div>
        </div>
    );

    const copyAndUpgradeForm = (
        <div className="box" style={boxStyle}>
            <p style={{ marginBottom: 20 }}>
                <span className="is-size-5">
                    Duplicate existing page
                </span>
                {' - Duplicate page to create appeal'}
                <br />
                <span className="is-size-7">
                    <i>(includes any updates and upgrades)</i>
                </span>
            </p>
            <div className="columns">
                <div className="column" style={{ flexGrow: 6, flexShrink: 7 }}>
                    <label htmlFor="NewTitle">
                        Enter new appeal name
                        <input style={{ maxWidth: 400 }} ref={inputRef} type="text" name="NewTitle" defaultValue={newtitle} className="input" onInput={inputHandler} />
                    </label>
                </div>
                <div className="column">
                    <button onClick={() => duplicateButtonHandler(true)} type="button" className="button is-info is-large" style={{ marginTop: 24 }}>Duplicate Page</button>
                </div>

            </div>
        </div>
    );

    const notification = (
        <div className="box" style={boxStyle}>
            {error}
        </div>
    );

    return !error
        ? (
            <>
                {upgradeForm}
                {copyAndUpgradeForm}
            </>
        )
        : notification;
};

DuplicateAppealForm.propTypes = {
    appealkey: PropTypes.string.isRequired,
    newtitle: PropTypes.string,
    legacyurl: PropTypes.string,
    baseurl: PropTypes.string,
    error: PropTypes.string,
};

DuplicateAppealForm.defaultProps = {
    legacyurl: '',
    newtitle: '',
    error: '',
    baseurl: `${window.location}`,
};

export default DuplicateAppealForm;
